import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { AppBar, Box, Card, CardActions, CardContent, CardMedia, Dialog, Grid, IconButton, Toolbar, Tooltip, Typography } from '@material-ui/core';
import Button from '../../CustomButtons/Button.js';
import { ThemeProvider } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import MakingDecisionsImg from '../../../assets/img/workbook_images/making_decisions.jpg';
import IndividualPlanImg from '../../../assets/img/workbook_images/individual_plan.jpg';
import BudgetingImg from '../../../assets/img/workbook_images/budgeting.jpg';
import HousingImg from '../../../assets/img/workbook_images/housing.jpg';
import HealthCareImg from '../../../assets/img/workbook_images/healthcare.jpg';
import RelationshipsImg from '../../../assets/img/workbook_images/relationships.jpg';
import TransportationImg from '../../../assets/img/workbook_images/transportation.jpg';
import MovingImg from '../../../assets/img/workbook_images/moving.jpg';
import PleaseWait from '../../Shared/PleaseWait';
import useErrorAlert from '../../../hooks/useErrorAlert';
import theme from '../../../styles/theme';
import Link from '@material-ui/core/Link';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import HomeIcon from '@material-ui/icons/Home';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import { btLightOrange } from '../../../assets/jss/nextjs-material-kit-pro';
import StarImg from '../../../assets/img/workbook_images/star.svg';
import TrophyOutlinedImg from '../../../assets/img/workbook_images/trophy-outlined.svg';
import TrophyOrangeImg from '../../../assets/img/workbook_images/trophy-orange.svg';
import { StyledBoxStar, StyledBoxTrophy, useWorkbookModuleStyles } from './WorkbookModule.styles';
import { calculateModuleCompletion } from '../../../util/workbookUtils';
import Paper from '@material-ui/core/Paper';
import { isAdmin, isAdminOrFHT } from '../../../util/miscUtil';

const COURSE_IMAGE_MAP = {
  'making_decisions.jpg': MakingDecisionsImg,
  'individual_plan.jpg': IndividualPlanImg,
  'budgeting.jpg': BudgetingImg,
  'housing.jpg': HousingImg,
  'healthcare.jpg': HealthCareImg,
  'relationships.jpg': RelationshipsImg,
  'transportation.jpg': TransportationImg,
  'moving.jpg': MovingImg,
};

const CARD_MEDIA_STYLE = {
  position: 'relative',
  zIndex: 10,
  borderRadius: '0 0 15px 15px',
};

const WorkbookModule = inject(
  'AuthStore',
  'eLearningStore'
)(
  observer((props) => {
    const authStore = props.AuthStore;
    const eLearningStore = props.eLearningStore;
    const moduleCourseList = props.moduleCourseList;
    const workbookModule = props.workbookModule;
    const workbookModuleNumber = props.workbookModuleNumber;
    const fetchCourseCompletionStatus = props.fetchCourseCompletionStatus;
    const setPage = props.setPage;
    const classes = props.classes;
    const allResources = props.allResources;
    const roles = props.roles;
    const fetchWorkbook = props.fetchWorkbook;
    const senior = props.senior;
    const alumni = props.alumni;
    const handleReturnToSeniorList = props.handleReturnToSeniorList;
    const handleGotoAdminHomepage = props.handleGotoAdminHomepage;
    const handleSetButtonColorByRegisteredCompletion = props.handleSetButtonColorByRegisteredCompletion;
    const isCourseLocked = props.isCourseLocked;
    const isStaffMode = props.isStaffMode !== undefined ? props.isStaffMode : false;
    const isFHTMode = props.isFHTMode !== undefined ? props.isFHTMode : false;

    window.scrollTo({ top: 0 });

    const [viewCourseOpen, setViewCourseOpen] = useState(false);
    const [currentCourse, setCurrentCourse] = useState(null);
    const [updatedCourses, setUpdatedCourses] = useState({});
    const showErrorAlert = useErrorAlert();

    const useWorkbookModuleStylesClasses = useWorkbookModuleStyles();

    useEffect(() => {
      if (typeof document !== 'undefined') {
        const elements = document.getElementsByClassName('osano-cm-widget');
        if (elements) {
          for (let i = 0; i < elements.length; i++) {
            elements[i].classList.add('osano-cm-widget-special');
          }
        }
      }
      return () => {
        if (typeof document !== 'undefined') {
          const elements = document.getElementsByClassName('osano-cm-widget');
          if (elements) {
            for (let i = 0; i < elements.length; i++) {
              elements[i].classList.remove('osano-cm-widget-special');
            }
          }
        }
      };
    }, []);

    useEffect(() => {
      if (eLearningStore.error) {
        showErrorAlert(eLearningStore.error, () => {
          eLearningStore.clearError();
        });
      }
    }, [eLearningStore.error]);

    const getImage = (filename) => {
      const image = COURSE_IMAGE_MAP[filename];

      if (!image) {
        return '';
      }

      return <CardMedia component='img' alt='course image' height='auto' image={image} style={CARD_MEDIA_STYLE} />;
    };

    const handleCloseCourse = async () => {
      setViewCourseOpen(false);
      if (isStaffMode || isFHTMode) {
        setCurrentCourse(null);
        return;
      }
      if (!currentCourse?.completed) {
        const activeUserId = senior?.userId ?? authStore?.user?.result?.id;
        const hasUpdate = await eLearningStore.updateCourseStatus(authStore, currentCourse?.course_id, activeUserId, currentCourse?.e_course_id);
        if (hasUpdate) {
          fetchWorkbook();
        }
      }
      setCurrentCourse(null);
    };

    const displayLongDescription = (desc) => {
      if (desc && desc.length > 150) {
        return desc?.substring(0, 150) + '...';
      } else {
        return desc;
      }
    };

    const NUMBER_OF_STARS = 6;
    const PERCENT_COMPLETE = calculateModuleCompletion(moduleCourseList);
    const trophyImgSrc = PERCENT_COMPLETE === 100 ? TrophyOrangeImg : TrophyOutlinedImg;

    const watchedCourseLabel = <span className={classes.statusIconAndText}>Watch</span>;

    return (
      <>
        <summary
          className={classes.workbookHeaderContent}
          style={{
            paddingTop: '20px',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: {
                xs: '380px',
                sm: '540px',
                md: '868px',
                lg: '1320px',
                xl: '1320px',
              },
            }}
          >
            <Grid container spacing={0} direction='row'>
              <Grid item xs={12} align='left'>
                <summary style={{ paddingTop: 0, marginBottom: '30px' }}>
                  <Grid container direction='row' style={{ width: '100%' }}>
                    <Grid item xs={12} sm={6} md={5} lg={4} align='left' style={{ marginTop: '-20px' }}>
                      <div className={useWorkbookModuleStylesClasses.imageHeader}>{getImage(workbookModule.image_name)}</div>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={8} align='left' className={classes.moduleCenterHeader}>
                      <Grid container direction='row'>
                        <Grid container spacing={0} direction='row' style={{ width: '100%', marginTop: '15px' }}>
                          <Grid item xs={9} sm={9} md={9} lg={9} align='left' style={{ width: '100%' }}>
                            <div className={classes.headerContent}>
                              <div className={classes.headerText}>
                                <div className={classes.title}>
                                  Module {workbookModuleNumber}: {workbookModule.module_name}
                                </div>
                              </div>
                            </div>
                          </Grid>
                          <Grid item xs={3} sm={3} md={3} lg={3} align='center' className={useWorkbookModuleStylesClasses.setReturnButtonFrameSize}>
                            {!isAdminOrFHT(roles) && (
                              <div style={{ marginTop: '-10px' }}>
                                <Tooltip title='Click to return to the workbook modules list.' placement='bottom'>
                                  <IconButton color='inherit' aria-label='return home' onClick={() => setPage('workbook')}>
                                    <Paper className={classes.circleIcon}>
                                      <ArrowForwardIcon className={useWorkbookModuleStylesClasses.homeIcon} />
                                    </Paper>
                                  </IconButton>
                                </Tooltip>
                              </div>
                            )}
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            sm={12}
                            md={12}
                            lg={12}
                            align='left'
                            style={{ paddingTop: '5px', paddingBottom: '10px', paddingRight: '5px', marginRight: '10px' }}
                          >
                            <hr className={useWorkbookModuleStylesClasses.lineStyle} />
                          </Grid>
                          <Grid item xs={12} sm={12} md={12} lg={12} align='left' style={{ width: '95%', marginBottom: '10px', minHeight: '160px' }}>
                            <div className={classes.headerContent}>
                              <div className={classes.headerText} style={{ width: '95%' }}>
                                <span className={classes.introduction}>{workbookModule.module_description}</span>
                              </div>
                            </div>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </summary>
              </Grid>
            </Grid>
          </Box>
        </summary>

        <Grid direction='row' style={{ width: '100%' }} align='center'>
          <Grid item xs={12} style={{ position: 'relative' }}>
            <Box className={useWorkbookModuleStylesClasses.workbookModuleStars}>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: {
                    xs: '400px',
                    sm: '540px',
                    md: '785px',
                    lg: '1150px',
                    xl: '1300px',
                  },
                }}
              >
                <Grid container spacing={0} direction='row' style={{ width: '100%', visibility: isStaffMode || isFHTMode ? 'hidden' : 'visible' }}>
                  <Grid item sm={4} md={4} lg={4} xl={4} />
                  <Grid item xs={12} sm={11} md={10} lg={6} xl={5} align='left' style={{ width: '100%' }}>
                    <div>
                      <Box display='flex' alignItems='center' style={{ marginLeft: 0, paddingLeft: 0 }}>
                        <Typography className={useWorkbookModuleStylesClasses.completionText}>{`${PERCENT_COMPLETE}% Complete`}</Typography>
                        <Box
                          className={useWorkbookModuleStylesClasses.displayStars}
                          style={{
                            width: `${NUMBER_OF_STARS * 33 + 70 + 20}px`, // 33px per star + trophy + 28 widths
                          }}
                        >
                          <Box
                            style={{
                              position: 'absolute',
                              left: 0,
                              height: '100%',
                              width: `${PERCENT_COMPLETE}%`,
                              background: btLightOrange,
                              zIndex: 0,
                              transition: 'width 0.3s',
                            }}
                          />
                          {Array.from({ length: NUMBER_OF_STARS }).map((_, index) => {
                            const isFirst = index === 0;
                            const isLast = index === NUMBER_OF_STARS - 1;
                            return (
                              <StyledBoxStar
                                key={`star-${index}`}
                                style={{
                                  borderRight: isLast ? '1px solid white' : 'none',
                                  borderLeft: isFirst ? '1px solid white' : 'none',
                                  paddingLeft: isFirst ? '15px' : 'none',
                                  paddingRight: isLast ? '45px' : '45px',
                                  background: 'transparent',
                                  position: 'relative',
                                  zIndex: 1,
                                }}
                              >
                                <img
                                  src={StarImg}
                                  alt='Star'
                                  style={{
                                    width: '31px',
                                    height: '26px',
                                    objectFit: 'contain',
                                  }}
                                />
                              </StyledBoxStar>
                            );
                          })}
                          <StyledBoxTrophy key='trophy' style={{ position: 'relative', zIndex: 250 }}>
                            <img
                              src={trophyImgSrc}
                              alt='Trophy'
                              style={{
                                width: '80px',
                                height: '70px',
                                objectFit: 'contain',
                              }}
                            />
                          </StyledBoxTrophy>
                        </Box>
                      </Box>
                    </div>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <summary className={classes.menuRestricted}>
          {isAdminOrFHT(roles) && (
            <div className={classes.breadcrumbs}>
              <Breadcrumbs separator='›' aria-label='breadcrumbs' className={classes.alignCrumb}>
                <span className={classes.breadCrumbLink} onClick={(e) => handleGotoAdminHomepage()}>
                  <HomeIcon style={{ fontSize: '1.30rem' }} />
                  <span style={{ margin: '1px 0 0 10px' }}>Home</span>
                </span>
                {!isStaffMode && (
                  <span className={classes.breadCrumbLink} onClick={(event) => handleReturnToSeniorList()}>
                    {alumni ? 'Alumni' : 'High School Seniors'}
                  </span>
                )}
                {!isStaffMode && (
                  <span className={classes.breadCrumbLink} style={{ marginTop: '-1px' }} onClick={(e) => setPage('home')}>
                    {senior && senior.alumni_updated_first_name ? senior.alumni_updated_first_name : senior.first_name}{' '}
                    {senior && senior.alumni_updated_last_name ? senior.alumni_updated_last_name : senior.last_name}
                  </span>
                )}
                <span className={classes.breadCrumbLink} style={{ marginTop: '-1px' }} onClick={(e) => setPage('workbook')}>
                  {isStaffMode ? 'My Planning Workbook' : 'Planning Workbook'}
                </span>
                <span className={classes.breadCrumbText} style={{ marginTop: '-1px' }}>
                  {workbookModule.module_name}:{workbookModule.id}
                </span>
              </Breadcrumbs>
            </div>
          )}
          <div className={classes.workbookCenterMenu}>
            <Grid container spacing={3} direction='row' className={classes.menuRestricted}>
              <Grid item xs={12} align='left' className={classes.moduleGrid} style={{ width: '100%' }}>
                {moduleCourseList?.map((course) => {
                  // Use updated course data if it exists in state, otherwise use original
                  const displayCourse = updatedCourses[course.id] ? { ...course, registered: true } : course;
                  return (
                    <Card className={classes.courseCard}>
                      <CardContent className={classes.courseCardBody}>
                        <section className={classes.courseName}>{displayCourse.course_name}</section>
                      </CardContent>
                      <Grid item xs={12} sm={12} md={12} lg={12} align='left' className={classes.description}>
                        {displayLongDescription(displayCourse.course_description)}
                      </Grid>
                      <CardActions className={classes.moduleActionContainer}>
                        <section className={classes.courseButtonContainer}>
                          {!isStaffMode && !isFHTMode && isAdmin(roles) ? (
                            <div className={classes.adminStatusDisplay}>{fetchCourseCompletionStatus(displayCourse, true)}</div>
                          ) : (
                            <Button
                              data-testid={`workbook-course-action-${displayCourse.id}`}
                              size='small'
                              round
                              variant='contained'
                              onClick={async () => {
                                if (isStaffMode || isFHTMode || isCourseLocked(displayCourse, false) === false) {
                                  const launchUserId = senior?.userId ?? authStore?.user?.result?.id;
                                  const link = await eLearningStore.getScormLaunchLink(
                                    authStore,
                                    displayCourse.course_id,
                                    launchUserId,
                                    displayCourse.id
                                  );
                                  if (link) {
                                    setViewCourseOpen(true);
                                    setCurrentCourse({
                                      launchLink: link,
                                      name: displayCourse.course_name,
                                      course_id: displayCourse.course_id,
                                      e_course_id: displayCourse.id,
                                      completed: !!displayCourse.completed,
                                    });
                                    // Mark course as registered immediately to show "Continue" instead of "Get Started"
                                    setUpdatedCourses((prev) => ({
                                      ...prev,
                                      [displayCourse.id]: { ...displayCourse, registered: true },
                                    }));
                                  }
                                }
                              }}
                              className={!isStaffMode && !isFHTMode && isAdmin(roles) ? classes.workbookDisabledButton : classes.workbookWatchButton}
                              color={handleSetButtonColorByRegisteredCompletion(displayCourse, true)}
                              disabled={isStaffMode || isFHTMode ? false : isCourseLocked(displayCourse, false)}
                            >
                              {isStaffMode || isFHTMode ? watchedCourseLabel : fetchCourseCompletionStatus(displayCourse, false)}
                            </Button>
                          )}
                        </section>
                      </CardActions>
                    </Card>
                  );
                })}
              </Grid>
            </Grid>
          </div>
        </summary>

        {allResources && allResources.length > 0 && (
          <div className={classes.resourcesBackground}>
            <summary className={classes.resources}>
              <div className={classes.resourceHeader}>Related Resources</div>
              <Grid container spacing={3} className={classes.resourceMenu}>
                <Grid item xs={12} align='left' className={classes.resourceGrid}>
                  {allResources?.map((resource) => {
                    return (
                      <Card className={classes.resourceCard} key={resource.id}>
                        <CardContent className={classes.resourceCardBody}>
                          <div className={classes.resourceCardHeader}>
                            <section className={classes.resourceName}>{resource.resource_name}</section>
                          </div>
                          <section className={classes.resourceDescription}>{resource.resource_description.substring(0, 130)}</section>
                        </CardContent>
                        <CardActions className={classes.resourceActions}>
                          <Link href={resource.resource_url} target='_blank' style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <Button round color='info' size='sm' variant='contained' style={{ margin: 0 }} className={classes.resourceButton}>
                              <a style={{ color: '#FFF' }}>View</a>
                            </Button>
                          </Link>
                        </CardActions>
                      </Card>
                    );
                  })}
                </Grid>
              </Grid>
            </summary>
          </div>
        )}

        <Button link onClick={(e) => setPage('workbook')} className={classes.returnButton}>
          <a style={{ color: 'rgba(0, 85, 128, 1)' }}>
            <u>Return to Workbook</u>
          </a>
        </Button>

        <PleaseWait isLoading={eLearningStore.state?.loading} />

        <Dialog fullScreen open={viewCourseOpen} onClose={handleCloseCourse}>
          <ThemeProvider theme={theme}>
            <AppBar sx={{ position: 'relative' }} color='primary'>
              <Toolbar>
                <IconButton edge='start' color='inherit' onClick={handleCloseCourse} aria-label='close'>
                  <CloseIcon />
                </IconButton>
                <Typography sx={{ ml: 2, flex: 1 }} variant='h6' component='div'>
                  {currentCourse?.name}
                </Typography>
              </Toolbar>
            </AppBar>
          </ThemeProvider>
          {currentCourse?.launchLink && (
            <iframe title={currentCourse.name} src={currentCourse.launchLink} style={{ marginTop: 64, border: 'none' }} height='100%' width='100%' />
          )}
        </Dialog>
      </>
    );
  })
);

WorkbookModule.propTypes = {
  moduleCourseList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      course_name: PropTypes.string.isRequired,
      course_description: PropTypes.string,
      registered: PropTypes.bool,
      completed: PropTypes.bool,
      course_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  workbookModule: PropTypes.shape({
    module_name: PropTypes.string.isRequired,
    module_description: PropTypes.string,
    completion: PropTypes.number.isRequired,
  }).isRequired,
  workbookModuleNumber: PropTypes.number.isRequired,
  allResources: PropTypes.array.isRequired,
  fetchCourseCompletionStatus: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  roles: PropTypes.array.isRequired,
  fetchWorkbook: PropTypes.func.isRequired,
  senior: PropTypes.object.isRequired,
  alumni: PropTypes.bool,
  handleReturnToSeniorList: PropTypes.func.isRequired,
  handleGotoAdminHomepage: PropTypes.func.isRequired,
  handleSetButtonColorByRegisteredCompletion: PropTypes.func.isRequired,
  isCourseLocked: PropTypes.func.isRequired,
  isStaffMode: PropTypes.bool,
  isFHTMode: PropTypes.bool,
};

export default WorkbookModule;
